// Purpose: Provides app wide session for the admin app

import {
    AuthSessionData,
    authSessionDataDefault,
    PersistedKeys,
    ProvidePersistedSession,
} from "@venuepos/react-common";
import { useSession as createSession } from "@peteck/react-context-session";
import { AvailableLocale, DEFAULT_LOCALE, isLocale } from "locales";
import React, { ReactElement } from "react";

type AdminSessionData = AuthSessionData & {
    locale: AvailableLocale;
};

const adminDefaultData: AdminSessionData = {
    ...authSessionDataDefault,
    locale: getLang(),
};

const adminPersistKeys: PersistedKeys<AdminSessionData> = [
    "authToken",
    "locale",
];

export const useAdminSession = createSession<AdminSessionData>();

export function ProvideAdminSession({ children }: { children: ReactElement }) {
    return (
        <ProvidePersistedSession
            persistKeys={adminPersistKeys}
            defaultData={adminDefaultData}
            sessionContext={"admin-session"}
        >
            {children}
        </ProvidePersistedSession>
    );
}

// @TODO: Make this cross-platform
// Exported for testing: under Jest, navigator is a stub from jest.config.js,
// so the branches below are only reachable by calling these directly.
export function getLang(): AvailableLocale {
    const rawLang = getRawLang();
    // The raw browser locale (e.g. "fr-FR") isn't guaranteed to be one of
    // our supported locales. Fall back to DEFAULT_LOCALE ("en") rather than
    // any other locale, since that's also i18next's own `fallbackLng`
    // (see packages/locales/index.ts) - this keeps the session's locale
    // consistent with the language that's actually shown to the user.
    return rawLang !== undefined && isLocale(rawLang)
        ? rawLang
        : DEFAULT_LOCALE;
}

// Returns undefined when the platform doesn't tell us anything about the
// user's language; picking the fallback locale is left to getLang().
export function getRawLang(): string | undefined {
    // `typeof navigator` rather than `!navigator`: referencing an undeclared
    // global throws a ReferenceError before the check can run, and a missing
    // `navigator` (Node/SSR) is exactly the case this guard is here for.
    if (typeof navigator === "undefined") {
        return undefined;
    }
    // Both of these can be undefined outside a browser (e.g. React Native),
    // even though the DOM typings say otherwise.
    if (navigator.languages !== undefined) {
        return navigator.languages[0];
    }
    return navigator.language;
}
